import {
  type FC,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
} from "react";

import { useApiKeys } from "../hooks/useApiKeys";

export const REQUIRED_PROVIDERS = [
  "anthropic",
  "bedrock",
  "google",
  "openai",
  "vertex",
] as const;

export const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Anthropic",
  azure: "Azure AI Foundry",
  bedrock: "Amazon Bedrock",
  google: "Google Generative AI",
  openai: "OpenAI",
  vertex: "Vertex AI",
};

interface OrgApiKeysContextValue {
  missingProviders: string[];
  loading: boolean;
}

const OrgApiKeysContext = createContext<OrgApiKeysContextValue | undefined>(
  undefined,
);

export const OrgApiKeysProvider: FC<{
  children: ReactNode;
  organizationId: string | null;
}> = ({ children, organizationId }) => {
  const { keys, loading } = useApiKeys(organizationId);

  const missingProviders = useMemo(() => {
    const configured = new Set(
      keys.filter((k) => k.has_key).map((k) => k.provider),
    );
    return REQUIRED_PROVIDERS.filter((p) => !configured.has(p));
  }, [keys]);

  const value = useMemo(
    () => ({ loading, missingProviders }),
    [missingProviders, loading],
  );

  return (
    <OrgApiKeysContext.Provider value={value}>
      {children}
    </OrgApiKeysContext.Provider>
  );
};

export function useOrgApiKeys(): OrgApiKeysContextValue {
  const ctx = useContext(OrgApiKeysContext);
  if (!ctx) {
    throw new Error("useOrgApiKeys must be used within an OrgApiKeysProvider");
  }
  return ctx;
}
